import "../../../css/CrawlSources.css";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Button, Table, Tag, Dropdown, Menu, Modal, message, Tabs } from "antd";
import { PlusOutlined, MoreOutlined, ExclamationCircleOutlined, SyncOutlined } from "@ant-design/icons";
import TaxonomyManagement from "./TaxonomyManagement";
import { getSources, deleteSource, triggerCrawl } from "../../../services/polly/crawlSourceService";
import useCrawlNotifications from "./useCrawlNotifications";

function statusTag(status) {
  const map = {
    ACTIVE: { color: "green", label: "Active" },
    CRAWLING: { color: "blue", label: "Crawling" },
    ERROR: { color: "red", label: "Error" },
    IDLE: { color: "default", label: "Idle" },
    DISABLED: { color: "default", label: "Disabled" },
  };
  const s = map[status] || { color: "default", label: status || "Unknown" };
  return <Tag color={s.color}>{s.label}</Tag>;
}

function formatDate(dateStr) {
  if (!dateStr) return "--";
  const d = new Date(dateStr);
  return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function CrawlSources() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [liveData, setLiveData] = useState({}); // { [sourceId]: { found, ingested, failed } }
  const mountedRef = useRef(true);
  const history = useHistory();

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  const fetchSources = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSources();
      if (!mountedRef.current) return;
      setSources(Array.isArray(res) ? res : (res.content || []));
    } catch (err) {
      if (!mountedRef.current) return;
      console.warn("Failed to fetch crawl sources:", err);
      message.error("Failed to load crawl sources");
    }
    if (mountedRef.current) setLoading(false);
  }, []);

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  // --- WebSocket live updates ---
  const onProgress = useCallback((payload) => {
    const sid = String(payload.sourceId);
    if (payload.status === "COMPLETED" || payload.status === "STOPPED") {
      setLiveData((prev) => {
        const next = { ...prev };
        delete next[sid];
        return next;
      });
      fetchSources();
      return;
    }
    setLiveData((prev) => ({
      ...prev,
      [sid]: {
        found: payload.foundCount ?? 0,
        ingested: payload.ingestedCount ?? 0,
        failed: payload.failedCount ?? 0,
      },
    }));
  }, [fetchSources]);

  useCrawlNotifications({ onProgress });

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Delete crawl source?",
      icon: <ExclamationCircleOutlined />,
      content: `This will permanently delete "${record.name}".`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          await deleteSource(record.id);
          message.success("Crawl source deleted");
          fetchSources();
        } catch (err) {
          console.warn("Failed to delete crawl source:", err);
          message.error("Failed to delete crawl source");
        }
      },
    });
  };

  const handleRunNow = async (record) => {
    try {
      await triggerCrawl(record.id);
      message.success("Crawl started for " + record.name);
      fetchSources();
    } catch (err) {
      console.warn("Failed to trigger crawl:", err);
      message.error("Failed to start crawl");
    }
  };

  const columns = [
    {
      title: "Source",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <a onClick={() => history.push(`/polly/crawl-sources/${record.id}/dashboard`)} style={{ textDecoration: "none" }}>
          <div className="crawl-src-name">{name}</div>
          <div className="crawl-src-url">{record.crawlSeedUrl}</div>
        </a>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 180,
      render: (status, record) => {
        const live = liveData[String(record.id)];
        if (live || status === "CRAWLING") {
          return (
            <span>
              <Tag icon={<SyncOutlined spin />} color="blue">Running</Tag>
              {live && (
                <span style={{ fontSize: 11, color: "#555" }}>
                  {live.found}F / {live.ingested}I / {live.failed}E
                </span>
              )}
            </span>
          );
        }
        return statusTag(status);
      },
    },
    {
      title: "Schedule",
      dataIndex: "scheduleCron",
      key: "schedule",
      width: 140,
      render: (cron) => cron || "Manual",
    },
    {
      title: "Last Crawled",
      dataIndex: "lastCrawledAt",
      key: "lastCrawled",
      width: 170,
      render: (d) => formatDate(d),
    },
    {
      title: "Documents",
      dataIndex: "documentCount",
      key: "documents",
      width: 100,
      render: (count) => count ?? "--",
    },
    {
      title: "Actions",
      key: "actions",
      width: 60,
      render: (_, record) => {
        const menu = (
          <Menu>
            <Menu.Item key="dashboard" onClick={() => history.push(`/polly/crawl-sources/${record.id}/dashboard`)}>
              Dashboard
            </Menu.Item>
            <Menu.Item key="edit" onClick={() => history.push(`/polly/crawl-sources/${record.id}`)}>
              Edit
            </Menu.Item>
            <Menu.Item key="run" onClick={() => handleRunNow(record)}>
              Run now
            </Menu.Item>
            <Menu.Item key="jobs" onClick={() => history.push(`/polly/crawl-sources/${record.id}/jobs`)}>
              Past crawls
            </Menu.Item>
            <Menu.Item key="delete" danger onClick={() => handleDelete(record)}>
              Delete
            </Menu.Item>
          </Menu>
        );
        return (
          <Dropdown overlay={menu} trigger={["click"]}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  const showEmpty = !loading && sources.length === 0;

  const sourcesTab = showEmpty ? (
    <div className="crawl-src-empty">
      <p>No crawl sources configured yet.</p>
      <Button type="primary" icon={<PlusOutlined />} onClick={() => history.push("/polly/crawl-sources/new")}>
        Add Crawl Source
      </Button>
    </div>
  ) : (
    <>
      <div className="crawl-src-header">
        <h2>Crawl Sources</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => history.push("/polly/crawl-sources/new")}>
          Add Crawl Source
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={sources}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="middle"
        rowClassName={(record) =>
          liveData[String(record.id)] || record.status === "CRAWLING"
            ? "crawl-src-row-running"
            : ""
        }
      />
    </>
  );

  return (
    <div className="crawl-src-container">
      <div className="crawl-src-breadcrumb">
        <a onClick={() => history.push("/polly/documents")}>Polly</a> &rsaquo; <span>Crawl Sources</span>
      </div>
      <div className="crawl-src-body">
        <Tabs defaultActiveKey="sources" items={[
          { key: "sources", label: "Sources", children: sourcesTab },
          { key: "topics", label: "Topics", children: <TaxonomyManagement /> },
        ]} />
      </div>
    </div>
  );
}
